<template>
  <!-- ONE cohesive card -->
  <div class="bg-white overflow-hidden divide-base-200">
    <!-- Plans -->
    <section class="p-6 space-y-6">
      <PricingHeader />
      <PricingPlans :plans="plans" :popularId="popularId" />
    </section>

    <!-- Breakdown -->
    <section class="p-6">
      <PricingSummary
        :formattedTotalDueNow="formattedTotalDueNow"
        :billingInterval="billingInterval"
        :formattedMonthlyRow="formattedMonthlyRow"
        :formattedRecurringRow="formattedRecurringRow"
        :daysUntilNext="daysUntilNext"
        :monthlyNextDate="monthlyNextDate"
        :yearlyNextDate="yearlyNextDate"
        :formattedMonthlyNextDate="formattedMonthlyNextDate"
        :formattedYearlyNextDate="formattedYearlyNextDate"
        :showBreakdown.sync="showBreakdown"
        :hasOneTime="hasOneTime"
        :formattedOneTime="formattedOneTime"
        :hasMonthly="hasMonthly"
        :hasYearly="hasYearly"
        :formattedYearlyRow="formattedYearlyRow"
        :taxRate="taxRate"
        :formattedTaxOnOneTime="formattedTaxOnOneTime"
        :formattedTaxOnMonthly="formattedTaxOnMonthly"
        :formattedTaxOnYearly="formattedTaxOnYearly" />
    </section>

    <!-- Upcoming payments -->
    <section class="p-6 bg-base-50">
      <PricingNextPayments
        :formattedMonthlyRow="formattedMonthlyRow"
        :formattedYearlyRow="formattedYearlyRow"
        :formattedMonthlyNextDate="formattedMonthlyNextDate"
        :formattedYearlyNextDate="formattedYearlyNextDate"
        :daysUntilNext="daysUntilNext"
        :hasYearly="hasYearly"
        :billingInterval="billingInterval"
        :formattedAnnualRecurringTotal="annualRecurringTotalFormatted" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { defineProps, computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import PricingHeader from "./PricingHeader.vue";
import PricingPlans from "./PricingPlans.vue";
import PricingSummary from "./PricingSummary.vue";
import PricingNextPayments from "./PricingNextPayments.vue";
import type { PlanOption } from "@/features/planSelection/models/PlanOption";
import { BillingInterval } from "../../types/BillingInterval";

defineProps<{
  plans: PlanOption[];
  popularId?: string;
  formattedOneTime: string;
  formattedMonthlyRow: string;
  formattedYearlyRow: string;
  formattedRecurringRow: string;
  formattedTaxOnOneTime: string;
  formattedTaxOnRecurring: string;
  formattedTotalDueNow: string;
  hasOneTime: boolean;
  hasMonthly: boolean;
  hasYearly: boolean;
  taxRate: number;
  billingInterval: BillingInterval;
  formattedTaxOnMonthly: string;
  formattedTaxOnYearly: string;
  annualRecurringTotalFormatted: string;
}>();

// Local state
const showBreakdown = ref(false);

// Date calculations
const today = new Date();
const msInDay = 1000 * 60 * 60 * 24;
const monthlyNextDate = computed(() => {
  const d = new Date(today);
  d.setDate(d.getDate() + 30);
  return d;
});
const daysUntilNext = computed(() => Math.ceil((monthlyNextDate.value.getTime() - today.getTime()) / msInDay));
const formattedMonthlyNextDate = computed(() => {
  const locale = useI18n().locale.value === "nl" ? "nl-NL" : "en-US";
  return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric" }).format(
    monthlyNextDate.value
  );
});
const yearlyNextDate = computed(() => {
  const d = new Date(today);
  d.setFullYear(d.getFullYear() + 1);
  return d;
});
const formattedYearlyNextDate = computed(() => {
  const locale = useI18n().locale.value === "nl" ? "nl-NL" : "en-US";
  return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric" }).format(
    yearlyNextDate.value
  );
});
</script>
